import {
  buffers,
  type Channel,
  eventChannel,
} from 'redux-saga';
import {
  actionChannel, call, delay, put, race, take, takeLatest,
} from 'redux-saga/effects';
import { FetchableResourceStatus } from '~/_shared/types/fetchableResource/fetchableResource';
import {
  registerStatsEventsBroadcastListener, type StatsEvent,
} from '~/_shared/utils/api/broadcast.helpers';
import { flatten } from '~/_shared/utils/collections/collections';
import { noop } from '~/_shared/utils/function.helpers';
import { select } from '~/_shared/utils/saga/effects';
import { isMapPresentationalSelector } from '~/store/selectors/useMapInfoSelectors';
import { clientIdSelector } from '../selectors/useClientIdSelector';
import { userDataIsReadySelector } from '../userData/settings/userDataSettings.selectors';
import {
  USER_DATA_SETTING_READY_SET, USER_GET_DATA_SUCCESS, USER_WEB_SOCKET_BROADCAST_INITIALIZED,
} from '../userData/userData.actionTypes';
import {
  addOrUpdateBoundaryStatsItems, requestBoundaryStatsItemsByIdIfExist,
} from './stats.actionCreators';
import {
  boundaryStatsItemToBoundaryStatsRequestItem, processBoundaryStatsResponse,
} from './stats.helpers';
import {
  type BoundaryStatsRequestItem, type BoundaryStatsResponse, calculateBoundaryStats, getBoundaryStats,
} from './stats.repository';
import { boundaryGroupStatsSelector } from './stats.selectors';

export function* statsSagas() {
  yield takeLatest(USER_GET_DATA_SUCCESS, watchForLoadStats);
  yield takeLatest(USER_WEB_SOCKET_BROADCAST_INITIALIZED, registerStatsSocketEventsProcessor);
}

/*
 * When the status of any item is changed to FetchableResourceStatus.Requested,
 * this saga should handle loading if from the backend
*/
function* watchForLoadStats() {
  // Buffer latest map settings action when the saga is busy and not taking actions.
  const statsChannel: Channel<Action> = yield actionChannel([
    addOrUpdateBoundaryStatsItems,
    requestBoundaryStatsItemsByIdIfExist,
  ], buffers.sliding(1));

  while (true) {
    let isUserDataSettingsReady: boolean = yield select(userDataIsReadySelector);

    while (!isUserDataSettingsReady) {
      yield take(USER_DATA_SETTING_READY_SET);
      isUserDataSettingsReady = yield select(userDataIsReadySelector);
    }

    const clientId: number | null = yield select(clientIdSelector);

    if (!clientId) {
      continue;
    }

    yield take(statsChannel);

    while (true) {

      const { debounce } = yield race({
        debounce: delay(1000),
        latestAction: take(statsChannel),
      });

      if (debounce) {
        break;
      }
    }

    const boundaryGroupsStats: ReturnType<typeof boundaryGroupStatsSelector> = yield select(boundaryGroupStatsSelector);
    const isMapPresentational: boolean = yield select(isMapPresentationalSelector);

    const statsPerBoundary = Array.from(boundaryGroupsStats.values());
    const allStats = flatten(
      statsPerBoundary.map((stats) => Array.from(stats.values()))
    );

    const statsToFetch = allStats
      .filter((stat) => (stat.data.status === FetchableResourceStatus.Requested));

    if (statsToFetch.length === 0) {
      continue;
    }

    yield put(addOrUpdateBoundaryStatsItems(statsToFetch.map((stat) => ({
      ...stat,
      data: {
        status: FetchableResourceStatus.Loading,
      },
    }))));

    const queryItems: ReadonlyArray<BoundaryStatsRequestItem> = statsToFetch.map(
      boundaryStatsItemToBoundaryStatsRequestItem
    );

    let itemsToCalculate: BoundaryStatsRequestItem[] = [];

    try {
      const response: BoundaryStatsResponse = yield call(getBoundaryStats, clientId, queryItems);

      const {
        itemsToUpdate, requestsWithoutResponse,
      } = processBoundaryStatsResponse(response, queryItems, allStats);

      itemsToCalculate = requestsWithoutResponse;

      if (itemsToUpdate.length > 0) {
        yield put(addOrUpdateBoundaryStatsItems(itemsToUpdate));
      }
    }
    catch (e) {
      // set all requested stats to error state
      const itemsToUpdate = statsToFetch.map((stat) => ({
        ...stat,
        data: {
          status: FetchableResourceStatus.NotFound,
        },
      }));
      if (itemsToUpdate.length > 0) {
        yield put(addOrUpdateBoundaryStatsItems(itemsToUpdate));
      }
    }

    // For items that were requested but not returned,
    // we want to try calculating them if the map is not presentational.
    // They might get added to charts without calculate being called,
    // so we want to make sure we calculate them at least once if they are requested.
    try {
      if (itemsToCalculate.length > 0 && !isMapPresentational) {
        const response: BoundaryStatsResponse = yield call(calculateBoundaryStats, clientId, itemsToCalculate);
        const {
          itemsToUpdate,
        } = processBoundaryStatsResponse(response, queryItems, allStats);

        if (itemsToUpdate.length > 0) {
          yield put(addOrUpdateBoundaryStatsItems(itemsToUpdate));
        }
      }
    }
    catch (e) {
      // set all requested stats to error state
      const statsToCalculate = allStats.filter((stat) => itemsToCalculate.some((item) => item.query_id === stat.id));
      const itemsToUpdate = statsToCalculate.map((stat) => ({
        ...stat,
        data: {
          status: FetchableResourceStatus.NotFound,
        },
      }));
      if (itemsToUpdate.length > 0) {
        yield put(addOrUpdateBoundaryStatsItems(itemsToUpdate));
      }
    }
  }
}

function* registerStatsSocketEventsProcessor() {
  const statsChannel: ReturnType<typeof registerStatsListeners> = yield call(registerStatsListeners);

  while (true) {
    const receivedIds = new Set<number>();

    const firstEvent: StatsEvent = yield take(statsChannel);
    if (firstEvent.id) {
      receivedIds.add(firstEvent.id);
    }

    while (true) {
      const { event, debounced } = yield race({
        event: take(statsChannel),
        debounced: delay(5000), // fetching recalculated stats is not time-sensitive, better catch more to stagger the data queries
      });

      if (event) {
        if (event.id) {
          receivedIds.add(event.id);
        }
      }

      if (debounced) {
        break;
      }
    }

    yield put(requestBoundaryStatsItemsByIdIfExist(Array.from(receivedIds)));

  }
}

const registerStatsListeners = () => {
  return eventChannel<StatsEvent>(emitter => {
    registerStatsEventsBroadcastListener({
      onStarted: noop, // for now if the user has already loaded stats the can keep showing the loaded ones until the next load
      onFinished: (e) => {
        emitter(e);
      },
      onError: noop,
    });

    return noop;
  }, buffers.expanding());
};
